import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";

interface MachineResult {
  machineId: number;
  machineModelName: string;
  machineHashrateTh: number;
  machinePower: number;
  btcAmountPaidToCustomer: number;
  btcAmountPaidToCustomerInUsd: number;
  hostingRevenueUsd: number;
  machinePriceUsd: number;
  daysMined: number;
  endDate: string;
}

interface MachinesResultModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  content?: React.ReactNode;
  machineData?: MachineResult[];
}

export default function MachinesResultModal({
  open,
  onClose,
  title = "Machine Results",
  content,
  machineData = [],
}: MachinesResultModalProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatBTC = (value: number) => value.toFixed(8);

  const totals = machineData.reduce(
    (acc, machine) => ({
      totalBTC: acc.totalBTC + machine.btcAmountPaidToCustomer,
      totalUSD: acc.totalUSD + machine.btcAmountPaidToCustomerInUsd,
      totalHosting: acc.totalHosting + machine.hostingRevenueUsd,
      totalMachinePrice: acc.totalMachinePrice + machine.machinePriceUsd,
      totalDays: acc.totalDays + machine.daysMined,
    }),
    {
      totalBTC: 0,
      totalUSD: 0,
      totalHosting: 0,
      totalMachinePrice: 0,
      totalDays: 0,
    }
  );

  // Function to download CSV
  const downloadCSV = () => {
    if (!machineData.length) return;

    const headers = [
      "Machine Model",
      "Hashrate (TH)",
      "Power (W)",
      "BTC Paid",
      "USD Value",
      "Hosting Revenue",
      "Machine Price",
      "Days Mined",
      "End Date",
    ];

    const rows = machineData.map((m) => [
      m.machineModelName,
      m.machineHashrateTh,
      m.machinePower,
      m.btcAmountPaidToCustomer,
      m.btcAmountPaidToCustomerInUsd,
      m.hostingRevenueUsd,
      m.machinePriceUsd,
      m.daysMined,
      m.endDate,
    ]);

    // Add totals row
    rows.push([
      "TOTALS",
      machineData.reduce((sum, m) => sum + m.machineHashrateTh, 0),
      machineData.reduce((sum, m) => sum + m.machinePower, 0),
      totals.totalBTC,
      totals.totalUSD,
      totals.totalHosting,
      totals.totalMachinePrice,
      totals.totalDays,
      "",
    ]);

    const csvContent =
      [headers, ...rows]
        .map((r) => r.join(","))
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "machine_results.csv";
    link.click();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: isDark ? "#121212" : "#fff",
          boxShadow: isDark
            ? "0 8px 32px rgba(0,0,0,0.6)"
            : "0 8px 32px rgba(0,0,0,0.1)",
          minHeight: "600px",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", py: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: isDark ? "#e0e0e0" : "#2C3E50" }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? "#b0b0b0" : "#666", mt: 1 }}>
          {machineData.length} machines found
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        {content || (
          <TableContainer
            component={Paper}
            sx={{
              background: isDark ? "#1e1e1e" : "#fff",
              maxHeight: "500px",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Machine Model",
                    "Hashrate (TH)",
                    "Power (W)",
                    "BTC Paid",
                    "USD Value",
                    "Hosting Revenue",
                    "Machine Price",
                    "Days Mined",
                    "End Date",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      align={["Hashrate (TH)", "Power (W)", "BTC Paid", "USD Value", "Hosting Revenue", "Machine Price", "Days Mined"].includes(header) ? "right" : "left"}
                      sx={{ fontWeight: "bold", bgcolor: isDark ? "#2d2d2d" : "#f5f5f5" }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {machineData.map((machine) => (
                  <TableRow
                    key={machine.machineId}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { bgcolor: isDark ? "#2a2a2a" : "#f9f9f9" },
                    }}
                  >
                    <TableCell>{machine.machineModelName}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${machine.machineHashrateTh} TH`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="right">{machine.machinePower.toLocaleString()} W</TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                      {formatBTC(machine.btcAmountPaidToCustomer)} BTC
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(machine.btcAmountPaidToCustomerInUsd)}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(machine.hostingRevenueUsd)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "bold", color: "#d84315" }}
                    >
                      {formatCurrency(machine.machinePriceUsd)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={machine.daysMined}
                        size="small"
                        color={machine.daysMined > 0 ? "success" : "default"}
                        variant={machine.daysMined > 0 ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>{new Date(machine.endDate).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!content && machineData.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" sx={{ color: isDark ? "#b0b0b0" : "#666" }}>
              No machine data available
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={downloadCSV}
          variant="contained"
          sx={{
            backgroundColor: isDark ? "#1976d2" : "#1565c0",
            "&:hover": { backgroundColor: isDark ? "#1565c0" : "#0d47a1" },
            borderRadius: 1,
            px: 4,
            py: 1,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.875rem",
          }}
        >
          Download CSV
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: isDark ? "#444" : "#6C757D",
            "&:hover": {
              backgroundColor: isDark ? "#555" : "#5A6268",
              boxShadow: isDark
                ? "0 2px 8px rgba(0,0,0,0.6)"
                : "0 2px 8px rgba(108,117,125,0.3)",
            },
            borderRadius: 1,
            px: 4,
            py: 1,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.875rem",
            minWidth: "120px",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
